import { useEffect } from 'react';

const BASE_URL = 'https://mehandimalabar.netlify.app';
const DEFAULT_IMAGE = `${BASE_URL}/NewProject.png`;

/**
 * useSEO – updates document title, meta description, canonical URL,
 * Open Graph and Twitter card tags for each page.
 *
 * @param {object} options
 * @param {string} options.title        Full page <title>
 * @param {string} options.description  Meta description (≤155 chars recommended)
 * @param {string} options.path         URL path, e.g. "/gallery"
 * @param {string} [options.image]      Absolute OG image URL (optional)
 */
function useSEO({ title, description, path, image }) {
    useEffect(() => {
        const fullUrl  = `${BASE_URL}${path}`;
        const ogImage  = image || DEFAULT_IMAGE;

        // --- <title> ---
        document.title = title;

        // helper: create or update a <meta> tag
        const setMeta = (selector, attr, value) => {
            let el = document.querySelector(selector);
            if (!el) {
                el = document.createElement('meta');
                const [attrName, attrValue] = selector.match(/\[([^=]+)="([^"]+)"\]/)
                    ? selector.match(/\[([^=]+)="([^"]+)"\]/).slice(1)
                    : [attr.split('=')[0], attr.split('=')[1]];
                el.setAttribute(attrName, attrValue);
                document.head.appendChild(el);
            }
            el.setAttribute('content', value);
        };

        // helper shorthand
        const setName     = (name, val) => setMeta(`meta[name="${name}"]`,     `name="${name}"`,     val);
        const setProperty = (prop, val) => setMeta(`meta[property="${prop}"]`, `property="${prop}"`, val);

        // --- Primary ---
        setName('description', description);
        setName('title',       title);

        // --- Canonical ---
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', fullUrl);

        // --- Open Graph ---
        setProperty('og:title',       title);
        setProperty('og:description', description);
        setProperty('og:url',         fullUrl);
        setProperty('og:image',       ogImage);

        // --- Twitter ---
        setName('twitter:title',       title);
        setName('twitter:description', description);
        setName('twitter:url',         fullUrl);
        setName('twitter:image',       ogImage);

    }, [title, description, path, image]);
}

export default useSEO;
